import { useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle, ArrowRight, CheckCircle2, Star, TrendingUp, Users,
  ClipboardList, Wrench, Sparkles, Info, ShieldAlert, MessageSquareWarning,
  Zap, Clock, ExternalLink, MapPin, Search, FileText, ListChecks, CalendarDays, BadgeCheck,
} from "lucide-react";
import { PRIORITY, OPP_PRIORITY, EFFORT, URGENCY, IMPACT_TYPE_TONE, scoreColor, scoreBand } from "@/lib/constants";

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

function FixMeta({ item }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {item.impact_note && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
          <TrendingUp className="w-3 h-3" /> {item.impact_note}
        </span>
      )}
      {item.effort && (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${EFFORT[item.effort] || EFFORT.Moderate}`}>
          <Zap className="w-3 h-3" /> {item.effort} to fix
        </span>
      )}
      {item.time_to_benefit && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
          <Clock className="w-3 h-3" /> {item.time_to_benefit}
        </span>
      )}
    </div>
  );
}

function FixThisButton({ onClick, testid, added }) {
  if (!onClick) return null;
  if (added) {
    return (
      <span data-testid={`${testid}-added`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
        <CheckCircle2 className="w-4 h-4" /> Added to Fix Plan
      </span>
    );
  }
  return (
    <button onClick={onClick} data-testid={testid}
      className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 transition-colors active:scale-95">
      <Wrench className="w-3.5 h-3.5" /> Fix This
    </button>
  );
}

const _band = (b) => {
  if (!b) return "—";
  const [lo, hi] = b;
  return (lo || 0) === (hi || 0) ? `$${(lo || 0).toLocaleString()}` : `$${(lo || 0).toLocaleString()}–$${(hi || 0).toLocaleString()}`;
};

export function UrgencyBadge({ urgency, testid }) {
  const u = URGENCY[urgency];
  if (!u) return null;
  return (
    <span data-testid={testid || `urgency-${String(urgency).toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide border ${u.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${u.dot}`} /> {u.label}
    </span>
  );
}

export function PotentialImpact({ financial, testid = "potential-impact" }) {
  if (!financial || !financial.monthly) return null;
  const tone = IMPACT_TYPE_TONE[financial.impact_type] || IMPACT_TYPE_TONE.POTENTIAL_REVENUE_OPPORTUNITY;
  return (
    <div className={`mt-3 rounded-lg border ${tone.bg} px-3.5 py-2.5 flex items-start justify-between gap-2`} data-testid={testid}>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{financial.impact_type_label || tone.label}</div>
        <div className={`font-heading text-lg font-bold leading-tight ${tone.tone}`}>
          {tone.sign}{_band(financial.monthly)}<span className="text-xs font-medium text-slate-400"> / month</span>
        </div>
        <div className={`text-sm font-semibold ${tone.tone}`}>
          {tone.sign}{_band(financial.annual)}<span className="text-xs font-medium text-slate-400"> / year</span>
        </div>
      </div>
      <span className="shrink-0 rounded px-2 py-0.5 bg-white/70 border border-slate-200 text-[9px] font-bold text-slate-500 uppercase tracking-wide">
        {financial.estimate_basis || "MODELED"}
      </span>
    </div>
  );
}

export function SectionTitle({ eyebrow, title, icon: Icon }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs uppercase tracking-[0.18em] mb-2">
          {Icon && <Icon className="w-4 h-4" />} {eyebrow}
        </div>
      )}
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
    </div>
  );
}

export function CategoryScores({ categories }) {
  return (
    <div className="space-y-5" data-testid="category-scores">
      {categories.map((c) => (
        <div key={c.key}>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-sm font-medium text-slate-700">{c.name}</span>
            <span className="text-sm font-semibold" style={{ color: scoreColor(c.score) }}>
              {c.score == null ? "N/A" : `${c.score}`}
              <span className="text-slate-400 font-normal"> · {scoreBand(c.score)}</span>
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <motion.div className="h-full rounded-full"
              style={{ backgroundColor: scoreColor(c.score) }}
              initial={{ width: 0 }} whileInView={{ width: `${c.score ?? 0}%` }}
              viewport={{ once: true }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} />
          </div>
          {c.explanation && <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{c.explanation}</p>}
        </div>
      ))}
    </div>
  );
}

export function TopOpportunities({ items, onFix, added }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-4" data-testid="top-opportunities">
      {items.map((op, i) => {
        const p = OPP_PRIORITY[op.priority] || OPP_PRIORITY.opportunity;
        return (
          <motion.div key={i} {...fade} transition={{ ...fade.transition, delay: i * 0.06 }}
            className={`bg-white rounded-xl border border-slate-200 border-l-4 ${p.ring} p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow`}
            data-testid={`opportunity-${i}`}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {op.urgency ? <UrgencyBadge urgency={op.urgency} /> : (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${p.chip}`}>
                  {p.dot} {p.label}
                </span>
              )}
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 tracking-tight">{op.title}</h3>
            <PotentialImpact financial={op.financial} testid={`opportunity-impact-${i}`} />
            {op.why_it_matters && (
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-800">Why it matters: </span>{op.why_it_matters}
              </p>
            )}
            {op.recommended_action && (
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-800">Recommended action: </span>{op.recommended_action}
              </p>
            )}
            <FixMeta item={op} />
            <FixThisButton onClick={onFix ? () => onFix(op) : null} added={added?.[op.code || op.title]} testid={`fix-opportunity-${i}`} />
          </motion.div>
        );
      })}
    </div>
  );
}

export function FindingsSection({ findings, onFix, added }) {
  const [filter, setFilter] = useState("All");
  const tabs = ["All", "Critical", "High", "Medium", "Low"];
  const list = filter === "All" ? findings : findings.filter((f) => f.priority === filter);
  return (
    <div data-testid="findings-section">
      <div className="flex flex-wrap gap-2 mb-5">
        {tabs.map((t) => {
          const count = t === "All" ? findings.length : findings.filter((f) => f.priority === t).length;
          const active = filter === t;
          return (
            <button key={t} onClick={() => setFilter(t)} data-testid={`filter-${t.toLowerCase()}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              {t} <span className={active ? "text-slate-300" : "text-slate-400"}>· {count}</span>
            </button>
          );
        })}
      </div>
      <div className="space-y-3">
        {list.map((f, i) => {
          const p = PRIORITY[f.priority] || PRIORITY.Low;
          return (
            <motion.div key={i} {...fade} transition={{ ...fade.transition, delay: Math.min(i * 0.03, 0.3) }}
              className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={`finding-${i}`}>
              <div className="flex items-start gap-3">
                <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${p.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {f.urgency ? <UrgencyBadge urgency={f.urgency} /> : (
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold border ${p.cls}`}>{p.label}</span>
                    )}
                    <span className="text-[11px] font-medium text-slate-400">Confidence: {f.confidence}</span>
                  </div>
                  <h4 className="font-heading font-semibold text-slate-900">{f.problem}</h4>
                  <PotentialImpact financial={f.financial} testid={`finding-impact-${i}`} />
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    <span className="font-semibold text-slate-700">Why it matters: </span>{f.why_it_matters}
                  </p>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    <span className="font-semibold text-slate-700">Recommended action: </span>{f.recommended_action}
                  </p>
                  <FixMeta item={f} />
                  <FixThisButton onClick={onFix ? () => onFix(f) : null} added={added?.[f.code || f.problem]} testid={`fix-finding-${i}`} />
                </div>
              </div>
            </motion.div>
          );
        })}
        {!list.length && <p className="text-sm text-slate-500">No findings in this category.</p>}
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  VERIFIED: "text-emerald-700 bg-emerald-50 border-emerald-200",
  UNVERIFIED: "text-amber-700 bg-amber-50 border-amber-200",
  NONE: "text-slate-500 bg-slate-50 border-slate-200",
};

export function DataBadge({ status, label }) {
  const s = status || "NONE";
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_STYLES[s] || STATUS_STYLES.NONE}`}>
      {label || s}
    </span>
  );
}

function ProvenanceLine({ provenance }) {
  if (!provenance) return null;
  const d = provenance.retrieved_at ? new Date(provenance.retrieved_at).toLocaleDateString() : null;
  return (
    <p className="text-[11px] text-slate-400 mt-3" data-testid="provenance-line">
      Source: {provenance.provider || provenance.source}{d ? ` · retrieved ${d}` : ""}
    </p>
  );
}

export function ReputationCard({ reputation }) {
  const r = reputation || {};
  const status = r.verification_status || (r.metrics ? "UNVERIFIED" : "NONE");
  const hasMetrics = !!r.metrics;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm h-full" data-testid="reputation-card">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          <h3 className="font-heading font-semibold text-slate-900">Reputation</h3>
        </div>
        <DataBadge status={status} label={r.source_label || status} />
      </div>
      {hasMetrics ? (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Metric label="Reviews" value={r.metrics?.review_count ?? "–"} />
            <Metric label="Rating" value={r.metrics?.rating ?? "–"} />
            <Metric label="Responded" value={r.metrics?.review_response_rate != null ? `${r.metrics.review_response_rate}%` : "–"} />
          </div>
          {r.message && <p className="text-xs text-slate-500 mb-3">{r.message}</p>}
          <div className="space-y-3">
            {(r.findings || []).map((f, i) => (
              <div key={i} className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquareWarning className="w-4 h-4 text-amber-600" />
                  <span className="text-[11px] font-semibold text-amber-700">POTENTIAL IMPACT: {f.impact}</span>
                </div>
                <p className="text-sm font-medium text-slate-800">{f.title}</p>
                <p className="text-xs text-slate-600 mt-1">{f.recommendation}</p>
              </div>
            ))}
          </div>
          <ProvenanceLine provenance={r.provenance} />
        </>
      ) : (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 flex gap-3">
          <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">{r.message}</p>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
      <div className="font-heading text-xl font-bold text-slate-900 leading-none">{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export function OutdatedInfoCard({ items }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm h-full" data-testid="outdated-info-card">
      <div className="flex items-center gap-2 mb-4">
        <ShieldAlert className="w-5 h-5 text-orange-500" />
        <h3 className="font-heading font-semibold text-slate-900">Outdated Information</h3>
      </div>
      <div className="space-y-3">
        {(items || []).map((it, i) => (
          <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-slate-800">{it.conflict}</span>
              <span className="text-[11px] font-medium text-slate-400">Confidence: {it.confidence}</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">{it.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MenuServicesCard({ menu }) {
  const m = menu || {};
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm h-full" data-testid="menu-services-card">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList className="w-5 h-5 text-indigo-500" />
        <h3 className="font-heading font-semibold text-slate-900">{m.label || "Products & Services"}</h3>
      </div>
      {m.findings?.length ? (
        <div className="space-y-3">
          {m.findings.map((f, i) => (
            <div key={i} className="rounded-lg border border-slate-100 p-3">
              <p className="text-sm font-medium text-slate-800">{f.problem}</p>
              <p className="text-xs text-slate-600 mt-1">{f.recommended_action}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700">{m.message}</p>
        </div>
      )}
    </div>
  );
}

export function CompetitorSection({ competitors }) {
  const c = competitors || {};
  const sourceText = { google_places_verified: "Verified · Google", self_reported: "Self-reported",
    unverified_ai: "AI-suggested", none: "No data" }[c.source] || "";
  const status = c.verification_status || "NONE";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid="competitor-section">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-700" />
          <h3 className="font-heading text-xl font-semibold text-slate-900">Competitive Position</h3>
        </div>
        <DataBadge status={status} label={sourceText || status} />
      </div>

      {c.businesses?.length > 0 && (
        <div className="mb-6" data-testid="competitor-verified-list">
          <div className="grid sm:grid-cols-2 gap-3">
            {c.businesses.map((b, i) => (
              <div key={i} data-testid={`competitor-verified-${i}`}
                className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-800 truncate">{b.name}</p>
                  {b.rating != null && (
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-slate-900 shrink-0">
                      <Star className="w-3.5 h-3.5 text-amber-500" />{b.rating}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {b.review_count != null ? `${b.review_count} Google reviews` : "Review count unavailable"}
                  {b.address ? ` · ${b.address}` : ""}
                </p>
              </div>
            ))}
          </div>
          <ProvenanceLine provenance={c.provenance} />
        </div>
      )}

      {c.available && c.comparison?.length ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-white text-left">
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold text-right">Your Business</th>
                <th className="px-4 py-3 font-semibold text-right">Competitor Avg</th>
              </tr>
            </thead>
            <tbody>
              {c.comparison.map((row, i) => {
                const behind = Number(row.your) < Number(row.competitor_avg);
                return (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-4 py-3 font-medium text-slate-700">{row.category}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${behind ? "text-red-600" : "text-emerald-600"}`}>{row.your}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{row.competitor_avg}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 flex gap-3 mb-6">
          <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">{c.message}</p>
        </div>
      )}

      {c.candidates?.length > 0 && (
        <div data-testid="competitor-candidates">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-indigo-600" />
            <h4 className="font-heading font-semibold text-slate-900">Suggested competitors to research</h4>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">{c.candidates_disclaimer}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {c.candidates.map((cand, i) => (
              <a key={i} href={cand.verify_url} target="_blank" rel="noopener noreferrer"
                data-testid={`competitor-candidate-${i}`}
                className="group flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3.5 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{cand.name}</p>
                  {cand.note && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{cand.note}</p>}
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 shrink-0 mt-0.5" />
              </a>
            ))}
          </div>
        </div>
      )}

      {c.maps_search_url && (
        <a href={c.maps_search_url} target="_blank" rel="noopener noreferrer" data-testid="competitor-maps-link"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700">
          <MapPin className="w-4 h-4" /> Find more competitors near you on Google Maps
        </a>
      )}
    </div>
  );
}

export function PotentialOpportunity({ potential }) {
  const p = potential || {};
  return (
    <motion.div {...fade} className="rounded-2xl bg-slate-900 text-white p-8 sm:p-10 relative overflow-hidden"
      data-testid="potential-opportunity">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="relative">
        <div className="flex items-center gap-2 text-indigo-300 font-semibold text-xs uppercase tracking-[0.18em] mb-3">
          <TrendingUp className="w-4 h-4" /> Potential Business Opportunity
        </div>
        <p className="text-lg sm:text-xl text-slate-100 leading-relaxed max-w-3xl font-heading font-medium">
          {p.message}
        </p>
      </div>
    </motion.div>
  );
}

export function LeadGenCTA({ onRescan, onGetFixed, onBookReview }) {
  return (
    <motion.div {...fade} className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-8 sm:p-12"
      data-testid="lead-gen-cta">
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs uppercase tracking-[0.18em] mb-3">
          <Wrench className="w-4 h-4" /> Want us to fix these problems?
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Your scan identified modeled opportunities to win back customers, time, or revenue — these are estimates, not actual losses.
        </h2>
        <p className="mt-3 text-slate-600">Let our team turn these findings into a done-for-you action plan.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={onGetFixed} data-testid="cta-get-fixed"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 transition-colors active:scale-95">
            Get My Business Fixed <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={onBookReview} data-testid="cta-book-review"
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-300 hover:border-slate-400 text-slate-800 font-semibold px-6 py-3 transition-colors">
            Book a Business Review
          </button>
          <button onClick={onRescan} data-testid="cta-run-another"
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-300 hover:border-slate-400 text-slate-800 font-semibold px-6 py-3 transition-colors">
            Run Another Scan
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export { AlertTriangle, Sparkles };

export function ExecutiveSummary({ summary, version, date, overview }) {
  if (!summary && !overview) return null;
  return (
    <motion.div {...fade} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid="executive-summary">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs uppercase tracking-[0.18em]">
          <FileText className="w-4 h-4" /> Executive Summary
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-400 font-medium">
          {date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {date}</span>}
          {version && <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-500">Report v{version}</span>}
        </div>
      </div>
      {overview?.text && (
        <div className="flex items-start gap-2 mb-4 pb-4 border-b border-slate-100" data-testid="business-overview">
          <p className="text-sm text-slate-500 leading-relaxed flex-1">{overview.text}</p>
          <DataBadge status={overview.identity?.verification_status} label={overview.identity?.verification_status === "VERIFIED" ? "Verified · Google" : "Unverified"} />
        </div>
      )}
      {summary && <p className="text-base sm:text-lg text-slate-700 leading-relaxed">{summary}</p>}
    </motion.div>
  );
}

export function NextSteps({ steps }) {
  if (!steps?.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid="next-steps">
      <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs uppercase tracking-[0.18em] mb-5">
        <ListChecks className="w-4 h-4" /> Recommended Next Steps
      </div>
      <ol className="space-y-4">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-4" data-testid={`next-step-${i}`}>
            <span className="grid place-items-center w-7 h-7 shrink-0 rounded-full bg-slate-900 text-white text-sm font-bold">{i + 1}</span>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed pt-0.5">{s}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function MiniStat({ value, label, accent = "text-slate-900" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className={`font-heading text-2xl font-bold leading-none ${accent}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1.5">{label}</div>
    </div>
  );
}

export function FixPlanSummary({ stats, onView }) {
  const s = stats || {};
  if (!s.tracked) return null;
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="fix-plan-summary">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-indigo-600" />
          <h3 className="font-heading text-xl font-semibold text-slate-900">Your Fix Plan</h3>
        </div>
        <button onClick={onView} data-testid="fix-plan-view"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
          Open Fix Task Board <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat value={money(s.estimated_opportunity)} label="Estimated opportunity" accent="text-slate-900" />
        <MiniStat value={money(s.completed_opportunity)} label="Completed (modeled)" accent="text-indigo-600" />
        <MiniStat value={money(s.confirmed_recovery)} label="Confirmed recovery" accent="text-emerald-600" />
        <MiniStat value={money(s.pending_verification)} label="Pending verification" accent="text-amber-600" />
      </div>
      <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
        <b>Completed</b> = modeled opportunity of fixes marked done (not yet measured). <b>Confirmed recovery</b> =
        owner-verified actual results only. Modeled figures are estimates, never guaranteed revenue or savings.
      </p>
    </div>
  );
}

export function ProvenanceLegend() {
  const items = [
    { label: "Verified", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", desc: "Directly measured from your live website, or retrieved from Google Places with a source and date." },
    { label: "Self-reported", cls: "text-sky-700 bg-sky-50 border-sky-200", desc: "Figures you entered yourself — shown as-is and not independently verified." },
    { label: "Estimated", cls: "text-amber-700 bg-amber-50 border-amber-200", desc: "Qualitative impact based on detected signals — an estimate, never a guaranteed result." },
    { label: "Unverified", cls: "text-slate-600 bg-slate-50 border-slate-200", desc: "AI-suggested items for you to research. No statistics are fabricated." },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="provenance-legend">
      <div className="flex items-center gap-2 mb-4">
        <BadgeCheck className="w-4 h-4 text-slate-500" />
        <h4 className="font-heading font-semibold text-slate-900 text-sm">How to read this report</h4>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <div key={it.label}>
            <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${it.cls}`}>{it.label}</span>
            <p className="text-xs text-slate-500 leading-relaxed">{it.desc}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-4">
        "Not available" means we don't have that data yet — e.g. connect Google Places for verified reviews and competitors.
      </p>
    </div>
  );
}
